import React from 'react'
import { Text as RNText, TextProps as RNTextProps, TextStyle } from 'react-native'
import { fontFamily as ff } from '../theme'
import { DSColor, Palette } from '@ppwr/poker'
import { useResponsive } from '@/screens/new-design/hooks'

export type DSTypographyVariant =
  | 'Body Large/Medium'
  | 'Body Large/Regular'
  | 'Body Medium/Medium'
  | 'Body Medium/Regular'
  | 'Body Small/Medium'
  | 'Body Small/Regular'
  | 'Caption/Bold'
  | 'Caption/Medium'
  | 'Caption/Regular'
  | 'Heading 1/Medium'
  | 'Heading 2/Medium'
  | 'Heading 3/Medium'
  | 'Heading 4/Medium'
  | 'Heading 5/Medium'
  | 'Heading 1/Regular'
  | 'Heading 2/Regular'
  | 'Heading 3/Regular'
  | 'Heading 4/Regular'
  | 'Heading 5/Regular'
  | 'Heading 6/Medium'
  | 'Mini/Bold'
  | 'Mini/Medium'
  | 'Mini/Regular'
  | 'Subheading/Medium'
  | 'Subheading/Regular'

type CustomTextStyle = Pick<
  TextStyle,
  'color' | 'lineHeight' | 'fontFamily' | 'fontSize' | 'fontWeight' | 'letterSpacing'
> & {
  fontSize: number
  lineHeight: number
}

export const Typography: Record<DSTypographyVariant, CustomTextStyle> = {
  'Body Large/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardMedium,
    fontSize: 18,
    fontWeight: '500',
    letterSpacing: -0.5,
    lineHeight: 21.6
  },
  'Body Large/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardRegular,
    fontSize: 18,
    fontWeight: '400',
    letterSpacing: -0.5,
    lineHeight: 21.6
  },
  'Body Medium/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardMedium,
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    lineHeight: 19.2
  },
  'Body Medium/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardRegular,
    fontSize: 16,
    fontWeight: '400',
    letterSpacing: -0.25,
    lineHeight: 19.2
  },
  'Body Small/Medium': {
    fontFamily: ff.GTAmericaStandardMedium,
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: -0.25,
    lineHeight: 16.8
  },
  'Body Small/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardRegular,
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.25,
    lineHeight: 16.8
  },
  'Caption/Bold': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaExtended,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 14.4
  },
  'Caption/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardMedium,
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 14.4
  },
  'Caption/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardRegular,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 14.4
  },
  'Heading 1/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckMedium,
    fontSize: 32,
    fontWeight: '500',
    lineHeight: 35.2
  },
  'Heading 1/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckRegular,
    fontSize: 32,
    fontWeight: '400',
    lineHeight: 35.2
  },
  'Heading 2/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckMedium,
    fontSize: 28,
    fontWeight: '500',
    lineHeight: 33.6
  },
  'Heading 2/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckRegular,
    fontSize: 28,
    fontWeight: '400',
    lineHeight: 33.6
  },
  'Heading 3/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckMedium,
    fontSize: 24,
    fontWeight: '500',
    lineHeight: 24
  },
  'Heading 3/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckRegular,
    fontSize: 24,
    fontWeight: '400',
    lineHeight: 24
  },
  'Heading 4/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckMedium,
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 26.4
  },
  'Heading 4/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckRegular,
    fontSize: 22,
    fontWeight: '400',
    lineHeight: 26.4
  },
  'Heading 5/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckMedium,
    fontSize: 20,
    fontWeight: '500',
    lineHeight: 23.2
  },
  'Heading 5/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckRegular,
    fontSize: 20,
    fontWeight: '400',
    lineHeight: 23.2
  },
  'Heading 6/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.FeatureDeckMedium,
    fontSize: 18,
    fontWeight: '500',
    lineHeight: 21.6
  },
  'Subheading/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardMedium,
    fontSize: 20,
    fontWeight: '500',
    lineHeight: 24
  },
  'Subheading/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardRegular,
    fontSize: 20,
    fontWeight: '400',
    lineHeight: 24
  },
  'Mini/Bold': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaExtended,
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
    lineHeight: 12
  },
  'Mini/Medium': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardMedium,
    fontSize: 10,
    fontWeight: '500',
    letterSpacing: 0.5,
    lineHeight: 12
  },
  'Mini/Regular': {
    color: Palette['Global/Black'],
    fontFamily: ff.GTAmericaStandardRegular,
    fontSize: 10,
    fontWeight: '400',
    lineHeight: 12
  }
}

export interface TextProps extends RNTextProps {
  variant: DSTypographyVariant
  color?: DSColor
  textAlign?: TextStyle['textAlign']
  uppercase?: boolean
}

export const Text: React.FC<TextProps> = ({
  color = 'Global/Black',
  variant,
  textAlign,
  style,
  uppercase,
  ...props
}) => {
  return (
    <RNText
      style={[
        Typography[variant],
        { color: Palette[color], textAlign },
        uppercase ? { textTransform: 'uppercase' } : {},
        style
      ]}
      {...props}
    />
  )
}

type TextScaledProps = TextProps & {
  scaleProps?: Parameters<typeof useResponsive>[0]
}
export const TextScaled = (props: TextScaledProps) => {
  const { variant, style, scaleProps, ...restProps } = props
  const { verticalScale } = useResponsive(scaleProps)

  const fontSize = Math.round(verticalScale(Typography[variant].fontSize))
  const lineHeight = Math.round(verticalScale(Typography[variant].lineHeight))

  const customStyle = {
    fontSize,
    lineHeight
  }

  return <Text variant={variant} {...restProps} style={[style, customStyle]} />
}
